import { useEffect, useMemo, useState } from "react";
import {
  AuditDetail,
  AuditFilter,
  AuditRow,
  fetchAuditDetail,
  useAuditFacets,
  useAuditList,
} from "../api/auditLogs";
import { ApiError } from "../api/client";
import { SortableTableHeader } from "../components/common/SortableTableHeader";
import {
  actionGroup,
  labelActionType,
  labelEntityType,
} from "./auditLabels";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./AuditLogsPage.css";

function formatDateTime(value: string): string {
  try {
    const d = new Date(value);
    return d.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return value;
  }
}

function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

type AuditSortField = "created_at" | "action_type" | "entity_type" | "actor_name" | "branch_name";
type AuditSortDir = "asc" | "desc";

function formatActorBranch(name: string | null | undefined): string {
  return name?.trim() || "Kurum geneli";
}

function formatActorOptionLabel(actor: {
  full_name: string | null;
  email: string | null;
  user_id: string;
  branch_name?: string | null;
}): string {
  const name = actor.full_name || actor.email || `${actor.user_id.slice(0, 8)}...`;
  const branch = actor.branch_name?.trim();
  return branch ? `${name} (${branch})` : name;
}

function sortAuditRows(rows: AuditRow[], sortBy: AuditSortField, sortDir: AuditSortDir): AuditRow[] {
  const sorted = [...rows];
  const dir = sortDir === "asc" ? 1 : -1;
  sorted.sort((a, b) => {
    let av: string | number = "";
    let bv: string | number = "";
    switch (sortBy) {
      case "created_at":
        av = new Date(a.created_at).getTime();
        bv = new Date(b.created_at).getTime();
        break;
      case "action_type":
        av = a.action_type;
        bv = b.action_type;
        break;
      case "entity_type":
        av = a.entity_type;
        bv = b.entity_type;
        break;
      case "actor_name":
        av = (a.actor_full_name || a.actor_email || "").toLowerCase();
        bv = (b.actor_full_name || b.actor_email || "").toLowerCase();
        break;
      case "branch_name":
        av = formatActorBranch(a.actor_branch_name).toLowerCase();
        bv = formatActorBranch(b.actor_branch_name).toLowerCase();
        break;
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
  return sorted;
}

function ActionBadge({ action }: { action: string }) {
  const group = actionGroup(action);
  return (
    <span className={`kp-audit__badge kp-audit__badge--${group}`}>
      {labelActionType(action)}
    </span>
  );
}

function JsonView({ data, title }: { data: unknown; title: string }) {
  if (data === null || data === undefined || (typeof data === "object" && Object.keys(data as object).length === 0)) {
    return (
      <div className="kp-audit__json">
        <header>{title}</header>
        <div className="kp-audit__json-empty">- (Boş)</div>
      </div>
    );
  }
  return (
    <div className="kp-audit__json">
      <header>{title}</header>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
}

function DetailDrawer({
  row,
  onClose,
}: {
  row: AuditRow | null;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AuditDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    setDetail(null);
    setError(null);
    setLoading(true);
    fetchAuditDetail(row.id)
      .then((res) => setDetail(res))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Kayıt Detayı Alınamadı."),
      )
      .finally(() => setLoading(false));
  }, [row]);

  if (!row) return null;

  return (
    <div className="kp-audit-drawer-backdrop" onClick={onClose}>
      <div className="kp-audit-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="kp-audit-drawer__header">
          <div>
            <ActionBadge action={row.action_type} />
            <div className="kp-audit-drawer__meta">
              <span>{labelEntityType(row.entity_type)}</span>
              <span>·</span>
              <span className="kp-audit__mono">{row.entity_id.slice(0, 8)}...</span>
            </div>
          </div>
          <button className="kp-audit-drawer__close" onClick={onClose}>
            x
          </button>
        </header>
        <div className="kp-audit-drawer__sub">
          <div>
            <span className="kp-muted">Tarih:</span> {formatDateTime(row.created_at)}
          </div>
          <div>
            <span className="kp-muted">Kim:</span>{" "}
            {row.actor_full_name || row.actor_email || row.actor_user_id.slice(0, 8) + "..."}
            {row.actor_email && row.actor_full_name && (
              <span className="kp-muted"> ({row.actor_email})</span>
            )}
            {row.actor_branch_name && (
              <span className="kp-muted"> · {formatActorBranch(row.actor_branch_name)}</span>
            )}
          </div>
        </div>
        <div className="kp-audit-drawer__body">
          {loading && <div className="kp-loading">{UI_LOADING}</div>}
          {error && <div className="kp-error">{error}</div>}
          {detail && (
            <div className="kp-audit-drawer__diff">
              <JsonView title="Önce (before_data)" data={detail.before_data} />
              <JsonView title="Sonra (after_data)" data={detail.after_data} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AuditLogsPage() {
  const facets = useAuditFacets();

  const [filter, setFilter] = useState<AuditFilter>({});
  const [sortBy, setSortBy] = useState<AuditSortField>("created_at");
  const [sortDir, setSortDir] = useState<AuditSortDir>("desc");

  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(JSON.stringify([filter, pageSize]));
  const list = useAuditList({ ...filter, limit: pageSize, offset });
  const [activeRow, setActiveRow] = useState<AuditRow | null>(null);

  const total = list.data?.total ?? 0;
  const rowCount = list.data?.rows.length ?? 0;
  const sortedRows = useMemo(
    () => sortAuditRows(list.data?.rows ?? [], sortBy, sortDir),
    [list.data?.rows, sortBy, sortDir],
  );

  const handleSort = (column: AuditSortField) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDir(column === "created_at" ? "desc" : "asc");
  };

  useRegisterListPageStats({
    label: "denetim kaydi",
    visible: rowCount,
    total,
    loading: list.loading,
  });

  const onChange = (patch: Partial<AuditFilter>) =>
    setFilter((prev) => ({ ...prev, ...patch }));

  const onReset = () => setFilter({});

  const quickRanges = useMemo(() => {
    const today = new Date();
    const ago = (days: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() - days);
      return d;
    };
    return [
      { label: "Bugun", start: today, end: today },
      { label: "Son 7 Gün", start: ago(6), end: today },
      { label: "Son 30 Gün", start: ago(29), end: today },
    ];
  }, []);

  return (
    <div className="kp-audit">
      <div className="kp-audit__header">
        <div>
          <h1>Denetim Kayıtları</h1>
          <p>
            {total} kayıt · Tüm Kritik Aksiyonların (Oluşturma, Güncelleme, İptal, Otomatik Plan, KM
            Değişikliği, Rapor Dışa Aktarma, Vb.) Tarihsel Kaydıdır. Bir Satıra Tıklayarak
            Önce/Sonra JSON&apos;unu İnceleyebilirsiniz.
          </p>
        </div>
        <div className="kp-audit__header-actions">
          <button className="kp-btn" onClick={list.reload} disabled={list.loading}>
            Yenile
          </button>
        </div>
      </div>

      <div className="kp-card kp-audit__filters">
        <div className="kp-audit__filters-row">
          <label className="kp-audit__field">
            <span>Varlık Tipi</span>
            <select
              className="kp-input"
              value={filter.entityType || ""}
              onChange={(e) => onChange({ entityType: e.target.value || undefined })}
            >
              <option value="">Tüm Varlıklar</option>
              {(facets.data?.entity_types || []).map((e) => (
                <option key={e.value} value={e.value}>
                  {labelEntityType(e.value)} ({e.count})
                </option>
              ))}
            </select>
          </label>

          <label className="kp-audit__field">
            <span>Aksiyon</span>
            <select
              className="kp-input"
              value={filter.actionType || ""}
              onChange={(e) => onChange({ actionType: e.target.value || undefined })}
            >
              <option value="">Tüm Aksiyonlar</option>
              {(facets.data?.action_types || []).map((a) => (
                <option key={a.value} value={a.value}>
                  {labelActionType(a.value)} ({a.count})
                </option>
              ))}
            </select>
          </label>

          <label className="kp-audit__field">
            <span>Yapan Kişi</span>
            <select
              className="kp-input"
              value={filter.actorUserId || ""}
              onChange={(e) => onChange({ actorUserId: e.target.value || undefined })}
            >
              <option value="">Herkes</option>
              {(facets.data?.actors || []).map((a) => (
                <option key={a.user_id} value={a.user_id}>
                  {formatActorOptionLabel(a)} ({a.count})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="kp-audit__filters-row">
          <label className="kp-audit__field">
            <span>Baslangic</span>
            <input
              type="date"
              className="kp-input"
              value={filter.startDate || ""}
              onChange={(e) => onChange({ startDate: e.target.value || undefined })}
            />
          </label>
          <label className="kp-audit__field">
            <span>Bitiş</span>
            <input
              type="date"
              className="kp-input"
              value={filter.endDate || ""}
              onChange={(e) => onChange({ endDate: e.target.value || undefined })}
            />
          </label>

          <div className="kp-audit__field kp-audit__field--wide">
            <span>Arama (Isim / E-Posta / Sube / Aksiyon)</span>
            <input
              type="text"
              className="kp-input"
              maxLength={200}
              placeholder="Ornek: ahmet, Kadikoy, branch_, admin@"
              value={filter.q || ""}
              onChange={(e) => onChange({ q: e.target.value || undefined })}
            />
          </div>
        </div>

        <div className="kp-audit__filters-row kp-audit__filters-row--actions">
          {quickRanges.map((r) => (
            <button
              key={r.label}
              type="button"
              className="kp-btn kp-btn--ghost"
              onClick={() =>
                onChange({ startDate: toIsoDate(r.start), endDate: toIsoDate(r.end) })
              }
            >
              {r.label}
            </button>
          ))}
          <button type="button" className="kp-btn kp-btn--ghost" onClick={onReset}>
            Filtreleri Temizle
          </button>
        </div>
      </div>

      {list.error && <div className="kp-card kp-error kp-audit__error">{list.error}</div>}

      <div className="kp-card kp-audit__table-wrap">
        {list.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {!list.loading && (list.data?.rows.length ?? 0) === 0 && (
          <div className="kp-empty">Bu Filtreyle Eşleşen Denetim Kaydı Bulunamadı.</div>
        )}
        {!list.loading && (list.data?.rows.length ?? 0) > 0 && (
          <div className="kp-audit__table-scroll">
            <table className="kp-audit__table kp-data-table">
              <thead>
                <tr>
                  <SortableTableHeader
                    label="Tarih"
                    column="created_at"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Aksiyon"
                    column="action_type"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Varlik"
                    column="entity_type"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Yapan"
                    column="actor_name"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Sube"
                    column="branch_name"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.id}>
                    <td className="kp-audit__cell-date">{formatDateTime(r.created_at)}</td>
                    <td>
                      <ActionBadge action={r.action_type} />
                    </td>
                    <td>
                      <div>{labelEntityType(r.entity_type)}</div>
                      <div className="kp-audit__mono kp-muted">{r.entity_id.slice(0, 8)}...</div>
                    </td>
                    <td>
                      <div>{r.actor_full_name || "-"}</div>
                      {r.actor_email && <div className="kp-muted">{r.actor_email}</div>}
                    </td>
                    <td>{formatActorBranch(r.actor_branch_name)}</td>
                    <td>
                      <button
                        type="button"
                        className="kp-btn kp-btn--ghost"
                        onClick={() => setActiveRow(r)}
                      >
                        Detay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ListPaginationBar
          total={total}
          offset={offset}
          limit={pageSize}
          loading={list.loading}
          onOffsetChange={setOffset}
        />
      </div>

      <DetailDrawer row={activeRow} onClose={() => setActiveRow(null)} />
    </div>
  );
}
